import axios from 'axios';
import authService from './authService';
import { getValidTenantId } from '../utils/tenantUtils';
import emailService from './emailService';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper function to get headers with authentication and tenant ID
const getHeadersWithTenant = () => {
  const headers = authService.getAuthHeader();
  const tenantId = getValidTenantId({ useLocalStorage: true });
  
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  
  return headers;
};

// Improved API request function with better error handling
const safeApiRequest = async (method, url, data = null, fallbackValue = null) => {
  try {
    const headers = getHeadersWithTenant();
    console.log(`Making ${method.toUpperCase()} request to: ${API_URL}${url} with headers:`, headers);
    
    const config = { 
      headers,
      method,
      url: `${API_URL}${url}`
    };
    
    if (data && (method === 'post' || method === 'put' || method === 'patch')) {
      config.data = data;
      console.log(`Request payload:`, data);
    }
    
    const response = await axios(config);
    console.log(`Response from ${url}:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error(`API Request failed for ${url}:`, error);
    
    // Log more detailed error information
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
      
      // If the error is about missing tenant and we have fallback data, use it
      if (error.response.data && error.response.data.message === 'Tenant not found for this admin') {
        console.warn('Using fallback data due to missing tenant');
        return fallbackValue;
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Error request:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    
    // Handle specific error cases
    if (error.response) {
      // If the endpoint returns 404 or 403, it might not be implemented yet or permission issue
      if (error.response.status === 404 || error.response.status === 403) {
        console.warn(`Endpoint ${url} returned ${error.response.status}, using fallback value:`, fallbackValue);
        return fallbackValue;
      }
    }
    
    // For connection errors, also use fallback
    if (error.message && (error.message.includes('Network Error') || error.message.includes('timeout'))) {
      console.warn(`Network error for ${url}, using fallback value:`, fallbackValue);
      return fallbackValue;
    }
    
    // Otherwise, rethrow the error
    throw error.response?.data || error.message;
  }
};

// Helper functions for email notifications
const sendFinancialRequestEmailToAdmin = async (requestData, memberData) => {
  try {
    const emailData = {
      to: 'admin@example.com', // Replace with actual admin email from configuration
      subject: 'New Financial Assistance Request',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); padding: 20px; text-align: center; color: white;">
            <h1>New Financial Assistance Request 💰</h1>
          </div>
          <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
            <p>Hello Admin,</p>
            <p>A new financial assistance request has been submitted.</p>
            <div style="background-color: #f8f8f8; border-left: 4px solid #3a7bd5; padding: 15px; margin: 20px 0;">
              <p style="margin: 0; font-weight: bold;">Request Details:</p>
              <ul>
                <li><strong>Member:</strong> ${memberData?.firstName} ${memberData?.lastName}</li>
                <li><strong>Reason:</strong> ${requestData.reason}</li>
                <li><strong>Amount:</strong> $${requestData.amount ? requestData.amount.toFixed(2) : 'Not specified'}</li>
                <li><strong>Date:</strong> ${new Date().toLocaleDateString()}</li>
              </ul>
            </div>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${window.location.origin}/admin/financial-requests" 
                style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                View Request
              </a>
            </div>
            <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
            <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} Transform Portal</p>
          </div>
        </div>
      `
    };
    
    await emailService.sendEmail(emailData);
    console.log('Admin notification email sent successfully');
    return true;
  } catch (error) {
    console.error('Failed to send admin notification email:', error);
    return false;
  }
};

const sendStatusUpdateEmailToMember = async (requestData, memberEmail, status) => {
  try {
    // Determine the appropriate content based on status
    let subject, statusText, statusColor, statusEmoji, message, callToAction;
    
    switch(status) {
      case 'Review':
        subject = 'Your Financial Assistance Request is Under Review';
        statusText = 'Under Review';
        statusColor = '#FF9800'; // Orange
        statusEmoji = '📋';
        message = 'Your financial assistance request has been moved to review. Our team is currently evaluating your request and will provide a decision soon.';
        callToAction = {
          text: 'View Request Details',
          url: `${window.location.origin}/member/financial-requests/${requestData._id}`
        };
        break;
      case 'Approved':
        subject = 'Your Financial Assistance Request has been Approved';
        statusText = 'Approved';
        statusColor = '#4CAF50'; // Green
        statusEmoji = '✅';
        message = 'We are pleased to inform you that your financial assistance request has been approved. The funds will be disbursed soon.';
        callToAction = {
          text: 'View Approved Request',
          url: `${window.location.origin}/member/financial-requests/${requestData._id}`
        };
        break;
      case 'Declined':
        subject = 'Update on Your Financial Assistance Request';
        statusText = 'Not Approved';
        statusColor = '#e53935'; // Red
        statusEmoji = '❌';
        message = `We regret to inform you that your financial assistance request could not be approved at this time. Reason: ${requestData.comments?.[requestData.comments.length-1]?.text || 'Please contact support for more information.'}`;
        callToAction = {
          text: 'View Request Details',
          url: `${window.location.origin}/member/financial-requests/${requestData._id}`
        };
        break;
      case 'Disbursed':
        subject = 'Funds Have Been Disbursed for Your Financial Assistance Request';
        statusText = 'Funds Disbursed';
        statusColor = '#3a7bd5'; // Blue
        statusEmoji = '💰';
        message = `The funds for your approved financial assistance request have been disbursed. Amount: $${requestData.amount ? requestData.amount.toFixed(2) : 'Not specified'}. Please confirm when you have received the funds.`;
        callToAction = {
          text: 'Confirm Receipt',
          url: `${window.location.origin}/member/financial-requests/${requestData._id}`
        };
        break;
      default:
        subject = 'Update on Your Financial Assistance Request';
        statusText = status;
        statusColor = '#3a7bd5'; // Default blue
        statusEmoji = '📋';
        message = 'There has been an update to your financial assistance request. Please check the portal for details.';
        callToAction = {
          text: 'View Request Details',
          url: `${window.location.origin}/member/financial-requests/${requestData._id}`
        };
    }
    
    // Send the email using the emailService
    const notificationData = {
      to: memberEmail,
      subject,
      orgName: 'Financial Assistance Request',
      status: status.toLowerCase(),
      message,
      callToAction
    };
    
    await emailService.sendRequestStatusNotification(notificationData);
    console.log(`Member notification email for status ${status} sent successfully`);
    return true;
  } catch (error) {
    console.error(`Failed to send member notification email for status ${status}:`, error);
    return false;
  }
};

const financialRequestService = {
  // Create a new financial request
  createRequest: async (requestData, memberData = null) => {
    console.log('Creating financial request with data:', requestData);
    const response = await safeApiRequest('post', '/financial-requests', requestData);
    
    // Send notification email to admin
    if (response && response.success) {
      await sendFinancialRequestEmailToAdmin(requestData, memberData);
    }
    
    return response;
  },

  // Get all requests for the current member
  getMemberRequests: async () => {
    return safeApiRequest('get', '/financial-requests/my-requests', null, { data: [] });
  },

  // Get all requests (admin)
  getAllRequests: async (page = 1, limit = 10, status = '', tenantId = null) => {
    // Get tenant ID to ensure we're only fetching requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, financial requests may not be filtered correctly');
    }
    
    let queryString = `/financial-requests?page=${page}&limit=${limit}`;
    if (status) queryString += `&status=${status}`;
    if (tenantIdParam) queryString += `&tenantId=${tenantIdParam}`;
    
    console.log(`API request URL: ${queryString}`);
    
    return safeApiRequest(
      'get', 
      queryString,
      null,
      { data: [], totalPages: 0, currentPage: page }
    );
  },

  // Get a single request by ID
  getRequestById: async (requestId, tenantId = null) => {
    // Get tenant ID to ensure we're only fetching requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, financial request details may not be filtered correctly');
    }
    
    let queryString = `/financial-requests/${requestId}`;
    if (tenantIdParam) queryString += `?tenantId=${tenantIdParam}`;
    
    return safeApiRequest(
      'get', 
      queryString,
      null,
      { data: null }
    );
  },

  // Update request status with email notification
  updateRequestStatus: async (requestId, status, comment, amount = null, tenantId = null) => {
    // Get tenant ID to ensure we're only updating requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, request status updates may not be filtered correctly');
    }
    
    const payload = { status, comment };
    
    // Add amount if provided (for disbursement)
    if (amount !== null) {
      payload.amount = amount;
    }
    
    let queryString = `/financial-requests/${requestId}/status`;
    if (tenantIdParam) queryString += `?tenantId=${tenantIdParam}`;
    
    const response = await safeApiRequest('patch', queryString, payload);
    
    // Send notification email to member if status change is successful
    if (response && response.success && response.data) {
      const requestData = response.data;
      const memberEmail = requestData.member?.userId?.email || requestData.memberEmail;
      
      if (memberEmail) {
        await sendStatusUpdateEmailToMember(requestData, memberEmail, status);
      } else {
        console.warn('Could not send status update email: Member email not found');
      }
    }
    
    return response;
  },

  // Add a comment to a request
  addComment: async (requestId, text, tenantId = null) => {
    // Get tenant ID to ensure we're only adding comments to requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, comment additions may not be filtered correctly');
    }
    
    let queryString = `/financial-requests/${requestId}/comments`;
    if (tenantIdParam) queryString += `?tenantId=${tenantIdParam}`;
    
    return safeApiRequest('post', queryString, { text });
  },

  // Get request count summary (admin)
  getRequestCounts: async (tenantId = null) => {
    // Get tenant ID to ensure we're only counting requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, financial request counts may not be filtered correctly');
    }
    
    let queryString = '/financial-requests/counts';
    if (tenantIdParam) queryString += `?tenantId=${tenantIdParam}`;
    
    console.log(`Request counts API URL: ${queryString}`);
    
    return safeApiRequest('get', 
      queryString, 
      null, 
      {
        total: 0,
        requested: 0,
        inReview: 0,
        approved: 0,
        declined: 0,
        memberRejected: 0,
        disbursed: 0,
        completed: 0
      }
    );
  },

  // Get all requests for a specific member (admin)
  getMemberRequestsAdmin: async (memberId, tenantId = null) => {
    // Get tenant ID to ensure we're only fetching requests for the current tenant
    const tenantIdParam = tenantId || getValidTenantId({ useLocalStorage: true });
    if (!tenantIdParam) {
      console.warn('No tenant ID found, member financial requests may not be filtered correctly');
    }
    
    let queryString = `/financial-requests/member/${memberId}`;
    if (tenantIdParam) queryString += `?tenantId=${tenantIdParam}`;
    
    return safeApiRequest('get', queryString, null, { data: [] });
  }
};

export default financialRequestService;